import { smoothScrollIntoView } from '@domgen/base-design-system-2.0/animation/smoothScrollIntoView';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { ProgressBars } from '@domgen/base-design-system-2.0/molecules/ProgressBars';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { Span } from '@opentelemetry/api';
import { usePostHog } from '@posthog/react';
import { ComponentType, SVGProps, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CoffeeMakerIcon } from '../../assets/icons/appliances/coffeeMaker';
import { CookerIcon } from '../../assets/icons/appliances/cooker';
import { DishwasherIcon } from '../../assets/icons/appliances/dishwasher';
import { LaundryIcon } from '../../assets/icons/appliances/laundry';
import { RefrigeratorIcon } from '../../assets/icons/appliances/refrigerator';
import { SmallApplianceIcon } from '../../assets/icons/appliances/smallAppliance';
import { StandMixerIcon } from '../../assets/icons/appliances/standMixer';
import { ArrowLeft } from '../../assets/icons/ArrowLeft';
import { CircleExclamationIcon } from '../../assets/icons/CircleExclamationIcon';
import { PageBackgroundWrapper } from '../../components/PageBackgroundWrapper';
import { brandCode } from '../../constants/brandCode';
import { AppRoute } from '../../enums/appRoutes';
import { FormKey } from '../../enums/formKey';
import { StepKey } from '../../enums/stepKey';
import { StoreEvent } from '../../enums/storeEvent';
import { useOTEL } from '../../hooks/useOTEL';
import { CatalogueAppliance, CatalogueCategory } from '../../models/services/api/getCatalogue';
import { capturePostHogEvent } from '../../services/posthog/capture';
import { useApplianceFormData } from '../../store/selectors/useApplianceFormData';
import { useAppState } from '../../store/selectors/useAppState';
import { useUrlParams } from '../../store/selectors/useUrlParams';
import { store } from '../../store/store';
import { filterCatalogueForBrand } from '../../utils/catalogue/filterCatalogueForBrand';
import { getSystemCode } from '../../utils/getSystemCode';
import { getLandingPageExperiment } from '../../utils/posthog/getLandingPageExperiment';
import { isLandingPageJourney } from '../../utils/posthog/isLandingPageJourney';
import { getRegistriaUrl } from '../../utils/registria/getRegistriaUrl';

export const styles = {
  grid: 'grid gap-(--spacing-space-500) grid-cols-2 md:grid-cols-3',
  categoryGridRows: 'auto-rows-[120px]',
  applianceGridRows: 'auto-rows-[70px]',
  applianceTile:
    ' py-(--spacing-space-500) px-(--spacing-space-300) text-center border-1 cursor-pointer button-border-outline-default text-text-primary-500 flex justify-center items-center flex-col',
  applianceTileButton:
    'rounded-radius-200 border-button-border-outline-default disabled:bg-(--button-bg-outline-disabled-color) disabled:text-(--button-text-outline-disabled-color)',
  error: 'bg-(--bg-error) border-(--input-border-error-color)',
  focus:
    'focus-visible:border-0 focus-visible:outline-3 focus-visible:inset-ring-3 focus-visible:border-button-border-primary-focus focus-visible:outline-focus-900 focus-visible:inset-ring-focus-600',
  hover:
    'hover:text-button-text-outline-hover hover:bg-button-bg-outline-hover hover:[&_svg]:text-(--color-icon-button-outline-active)',
  active:
    'active:text-button-text-outline-active active:bg-button-bg-outline-active active:[&_svg]:text-(--color-icon-button-outline-active) active:bg-button-bg-outline-hover active:text-button-text-outline-active',
  selected:
    '[&_svg]:text-(--color-icon-button-outline-active) bg-button-bg-outline-active text-button-text-outline-active',
  unselected: 'button-border-outline-default [&_svg]:text-(--color-icon-button-outline-default)',
  canadaLink: 'text-center',

  selectedApplianceType: (isSelected: boolean) =>
    isSelected
      ? styles['selected'] + ' ' + 'px-space-200'
      : styles['unselected'] + ' ' + ' px-space-300',
};

// Icon assignments are deliberate in the case of the CoffeeMaker icon for the `sdas` (small domestic appliances)
// and `misc` categories, as there are no specific icons for those categories in the current catalogue
export const iconPallete: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  cook: CookerIcon,
  dish: DishwasherIcon,
  laun: LaundryIcon,
  misc: SmallApplianceIcon,
  rfrg: RefrigeratorIcon,
  sdas: CoffeeMakerIcon,
  stmx: StandMixerIcon,
};

export function ApplianceDetailsPage({
  stepNumber,
  totalSteps,
}: {
  stepNumber: number;
  totalSteps: number;
}) {
  const { t } = useTranslation(['applianceDetailsPage', 'common']);
  const { session, catalogue } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const { startSpan, recordError, addEvent } = useOTEL();
  const systemCode = getSystemCode(urlParams?.rs);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    formData[StepKey.APPLIANCE_DETAILS]?.[FormKey.SUPER_CATEGORY] || null,
  );
  const [selectedAppliance, setSelectedApplianceCode] = useState<string | null>(
    formData[StepKey.APPLIANCE_DETAILS]?.[FormKey.ITEM_CATEGORY] || null,
  );
  const [isApplianceError, setIsApplianceError] = useState<boolean>(false);
  const [isCategoryError, setIsCategoryError] = useState<boolean>(false);
  const posthog = usePostHog();
  const { experimentName, experimentVariant } = getLandingPageExperiment(posthog);
  const [applianceCategory, setApplianceCategory] = useState<string | undefined>(undefined);
  const [applianceName, setApplianceName] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const landingPageFlagEnabled = isLandingPageJourney(posthog);
  const scrollIntoView = useCallback((el: HTMLElement | null) => {
    if (el) smoothScrollIntoView(el);
  }, []);

  const [pageSpan, setPageSpan] = useState<Span | null>(null);
  const environment = import.meta.env.MODE;
  const canadaUrl = getRegistriaUrl(brandCode, environment ?? '');

  // Some catalogue categories shouldn't be displayed based on brand, so they're filtered out before
  // rendering. Every category/appliance lookup on this step reads from the filtered list so an
  // excluded category can't be selected or resolved. The category can be resolved from the model search results,
  // but if it's not in the filtered catalogue it won't be displayed on this step.

  const filteredCatalogue = useMemo(
    () => filterCatalogueForBrand(catalogue, brandCode),
    [catalogue, brandCode],
  );

  useEffect(() => {
    setSelectedCategory(formData[StepKey.APPLIANCE_DETAILS]?.[FormKey.SUPER_CATEGORY] || null);
    setSelectedApplianceCode(formData[StepKey.APPLIANCE_DETAILS]?.[FormKey.ITEM_CATEGORY] || null);
  }, [formData]);

  useEffect(() => {
    const routeViewSpan = startSpan('ui.route.view', {
      'app.route.path': window.location.pathname,
      'app.route.title': 'appliance details',
      'app.system.code': systemCode,
      'app.session.id': session?.id ?? 'unknown',
    });
    setPageSpan(routeViewSpan);
  }, []);

  useEffect(() => {
    gaPageView({
      pagename: 'appliance-type',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      experimentName,
      experimentVariant,
    });
  }, []);

  const errorMessage = () => {
    if (isApplianceError || isCategoryError) {
      return (
        <div className="text-error-600 mt-2 flex items-center gap-1">
          <CircleExclamationIcon />
          <p>{selectedCategory ? t('errors.selectType') : t('errors.selectCategory')}</p>
        </div>
      );
    }
  };

  const icon = (IconComponent: ComponentType<SVGProps<SVGSVGElement>>) => <IconComponent />;

  const appliances = filteredCatalogue?.find(
    (cat) => cat.categoryCode === selectedCategory,
  )?.appliances;

  const handleContinue = () => {
    gaEvent({
      pagename: 'appliance-type',
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: !selectedCategory || !selectedAppliance ? 'failed' : 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      appSessionID: session?.id ?? '',
      experimentName,
      experimentVariant,
    });

    if (applianceName) {
      capturePostHogEvent(
        posthog,
        `${selectedCategory?.toLowerCase()}-${[...applianceName.trim()].reduce((acc, current) => {
          if (current === ',') return acc;
          if (current === ' ' || current === '/') return acc + '-';
          return acc + current.toLowerCase();
        }, '')}-selected`,
        store.get(),
      );
    }

    addEvent('appliance-details-continue-clicked', {
      'appliance.selectedCategory': selectedCategory ?? 'none',
      'appliance.selectedAppliance': selectedAppliance ?? 'none',
    });

    if (!selectedCategory) {
      setIsCategoryError(true);
      return;
    }

    if (!selectedAppliance) {
      recordError(new Error('Appliance not selected'));
      setIsApplianceError(true);
      return;
    }

    capturePostHogEvent(posthog, 'appliance-details-continue-cta-clicked', store.get());
    pageSpan?.end();
    store.send({
      type: StoreEvent.NEXT,
      data: {
        [StepKey.APPLIANCE_DETAILS]: {
          [FormKey.SUPER_CATEGORY]: selectedCategory,
          [FormKey.ITEM_CATEGORY]: selectedAppliance,
        },
      },
    });
  };

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    gaEvent({
      pagename: 'appliance-type',
      eventCategory: 'appliance details form USA',
      eventAction: 'GoBack CTA',
      eventLabel: 'Go back',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      experimentName,
      experimentVariant,
    });
    capturePostHogEvent(posthog, 'appliance-details-back-clicked', store.get());
    navigate(AppRoute.LANDING);
  };

  const handleCategorySelect = (categoryCode: string) => {
    if (selectedCategory !== categoryCode) {
      setSelectedCategory(categoryCode);
      setSelectedApplianceCode(null);
      setIsCategoryError(false);

      const applianceCategoryName = filteredCatalogue?.find(
        (category) => category.categoryCode === categoryCode,
      )?.categoryName;

      setApplianceCategory(applianceCategoryName);
      gaEvent({
        pagename: 'appliance-type',
        eventCategory: 'appliance details form USA',
        eventAction: 'applianceCategoryCode',
        eventLabel: categoryCode,
        category: 'registration-pages',
        type: 'STANDARD',
        applianceCategory: applianceCategoryName ?? applianceCategory,
        appSessionID: session?.id,
        regSystemCode: systemCode,
        experimentName,
        experimentVariant,
      });
      addEvent('super-category-selected', {
        'appliance.selectedCategory': categoryCode,
      });
      capturePostHogEvent(posthog, 'super-appliance-selected', store.get());
    }
  };

  const handleApplianceSelect = (applianceCode: string) => {
    if (selectedAppliance !== applianceCode) {
      setSelectedApplianceCode(applianceCode);
      setIsApplianceError(false);

      const appliances =
        filteredCatalogue?.find((category) => category.categoryCode === selectedCategory)
          ?.appliances ?? [];

      const applianceName = appliances.find(
        (appliance) => appliance.applianceCode === applianceCode,
      )?.applianceName;

      setApplianceName(applianceName);

      gaEvent({
        pagename: 'appliance-type',
        eventCategory: 'appliance details form USA',
        eventAction: 'applianceCode',
        eventLabel: applianceCode,
        category: 'registration-pages',
        type: 'STANDARD',
        applianceCategory,
        appSessionID: session?.id,
        regSystemCode: systemCode,
        experimentName,
        experimentVariant,
      });
      addEvent('item-category-selected', {
        'appliance.selectedAppliance': applianceCode,
      });
      capturePostHogEvent(posthog, 'sub-appliance-selected', store.get());
    }
  };

  return (
    <PageBackgroundWrapper gapSize="500">
      <ProgressBars total={totalSteps} current={stepNumber} />
      <h1 className="text-h3 text-text-headline">{t('heading')}</h1>
      <p className="text-body-large-strong">{t('subHeadingCategory')}</p>
      <div className={`${styles['categoryGridRows']} ${styles['grid']}`}>
        {filteredCatalogue &&
          filteredCatalogue.map((category: CatalogueCategory) => (
            <button
              key={category.categoryCode}
              aria-pressed={selectedCategory === category.categoryCode}
              id={`category-${category.categoryCode.toLocaleLowerCase()}`}
              value={category.categoryCode}
              type="button"
              className={`w-full
                    ${styles['selectedApplianceType'](selectedCategory === category.categoryCode)}
                    ${isCategoryError && styles['error']}
                    ${styles['applianceTile']}
                    ${styles['applianceTileButton']}
                    ${styles['focus']}
                    ${styles['hover']}
                    ${styles['active']}
                   `}
              onClick={() => handleCategorySelect(category.categoryCode)}
            >
              <div className="mb-space-100">
                {icon(iconPallete[category.categoryCode.toLocaleLowerCase()] ?? SmallApplianceIcon)}
              </div>
              <p
                className={`${selectedCategory === category.categoryCode ? 'text-body-small-strong' : 'text-body-small'}`}
              >
                {category.categoryName}
              </p>
            </button>
          ))}
      </div>

      {appliances ? (
        <div ref={(el) => scrollIntoView(el)} className="mt-4">
          <p className="mb-4 text-body-large-strong">{t('subHeadingAppliance')}</p>
          <div className={`${styles['applianceGridRows']} ${styles['grid']}`}>
            {appliances.map((appliance: CatalogueAppliance) => (
              <button
                key={appliance.applianceCode}
                aria-pressed={selectedAppliance === appliance.applianceCode}
                id={`appliance-${appliance.applianceCode}`}
                value={appliance.applianceCode}
                type="button"
                className={`
                      ${styles['selectedApplianceType'](selectedAppliance === appliance.applianceCode)}
                      ${isApplianceError && styles['error']}
                      ${styles['applianceTile']}
                      ${styles['applianceTileButton']}
                      ${styles['focus']}
                      ${styles['hover']}
                    `}
                onClick={() => handleApplianceSelect(appliance.applianceCode)}
              >
                <p
                  className={`${selectedAppliance === appliance.applianceCode ? 'text-body-small-strong' : 'text-body-small'}`}
                >
                  {appliance.applianceName}
                </p>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {errorMessage()}
      {canadaUrl && (
        <div className={styles.canadaLink}>
          <a
            href={canadaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-body-link-strong"
          >
            {t('common:canadaLink')}
          </a>
        </div>
      )}

      <div className="flex flex-col w-full items-center md:flex-row-reverse md:justify-between">
        <div className="w-full md:w-fit mb-4">
          <Button onClick={handleContinue}>{t('common:continue')}</Button>
        </div>
        {landingPageFlagEnabled && (
          <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
            {t('common:back')}
          </TextLink>
        )}
      </div>
    </PageBackgroundWrapper>
  );
}
